import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { enlistGideon } from '../../services/gideonService';
import { getGideonWhatsAppInviteUrl } from '../../data/gideonConstants';

const AGE_GROUPS = ['13-17', '18-24', '25-34', '35+'];

const GideonEnlistmentForm = () => {
  const { currentUser, userData, setUserData } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    fullName: userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : '',
    homeAddress: userData?.addressLine1 || '',
    phone: userData?.phoneNumber || '',
    email: currentUser?.email || '',
    ageGroup: '',
    denomination: '',
    homeChurch: '',
    churchPhone: '',
    availability: '',
    prayerTripletParticipation: false,
    promotionsEmailOptIn: false,
    whatsappOptIn: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      navigate('/auth/login', { state: { from: '/gideon-force#enlist' } });
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await enlistGideon(currentUser.uid, formData);
      setUserData(prev => prev ? { ...prev, isGideonMember: true } : { isGideonMember: true });
      setSuccess(true);
    } catch (err) {
      console.error('Gideon enlistment error:', err);
      setError(t('gideon.enlistment.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const whatsappUrl = getGideonWhatsAppInviteUrl();

  if (success) {
    return (
      <div className="bg-emerald-950/40 border border-emerald-700/50 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">{t('gideon.enlistment.success')}</h3>
        <p className="text-sm text-gideon-sand/90 mb-4">{t('gideon.enlistment.successNext', 'Check your email for updates if you opted in. Stay close to the app prayer points.')}</p>
        {formData.whatsappOptIn && whatsappUrl && (
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 mb-4 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold transition-colors"
          >
            {t('gideon.enlistment.joinWhatsApp', 'Join WhatsApp group')}
          </a>
        )}
        {formData.whatsappOptIn && !whatsappUrl && (
          <p className="text-sm text-gideon-sand/80 mb-4 italic">{t('gideon.enlistment.whatsAppFollowUp', 'Our team will send you the WhatsApp invite.')}</p>
        )}
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="mt-2 px-6 py-2 bg-gideon-khaki text-gideon-ink rounded-lg font-semibold hover:bg-gideon-sand transition-colors"
        >
          {t('dashboard.viewGideonPage', 'Go to Dashboard')}
        </button>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center">
        <p className="text-amber-900 mb-4">{t('gideon.enlistment.signInToEnlist')}</p>
        <button
          onClick={() => navigate('/auth/login', { state: { from: '/gideon-force#enlist' } })}
          className="px-6 py-3 bg-prayer-red text-white rounded-xl font-medium hover:bg-prayer-red-light"
        >
          {t('navigation.signIn')}
        </button>
      </div>
    );
  }

  const copy = t('gideon.enlistment', {});
  const fields = copy.fields || {};
  const fieldClass =
    'w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-600 focus:ring-2 focus:ring-prayer-red focus:border-prayer-red';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gideon-parchment mb-1.5">{fields.fullName}</label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className={fieldClass}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gideon-parchment mb-1.5">{fields.phone}</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={fieldClass}
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gideon-parchment mb-1.5">{fields.email}</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={fieldClass}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gideon-parchment mb-1.5">{fields.homeAddress}</label>
        <input
          type="text"
          name="homeAddress"
          value={formData.homeAddress}
          onChange={handleChange}
          className={fieldClass}
          required
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gideon-parchment mb-1.5">{fields.ageGroup}</label>
          <select
            name="ageGroup"
            value={formData.ageGroup}
            onChange={handleChange}
            className={fieldClass}
            required
          >
            <option value="" className="text-gray-900">Select</option>
            {AGE_GROUPS.map(g => (
              <option key={g} value={g} className="text-gray-900">{g}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gideon-parchment mb-1.5">{fields.denomination}</label>
          <input
            type="text"
            name="denomination"
            value={formData.denomination}
            onChange={handleChange}
            className={fieldClass}
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gideon-parchment mb-1.5">{fields.homeChurch}</label>
          <input
            type="text"
            name="homeChurch"
            value={formData.homeChurch}
            onChange={handleChange}
            className={fieldClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gideon-parchment mb-1.5">{fields.churchPhone}</label>
          <input
            type="tel"
            name="churchPhone"
            value={formData.churchPhone}
            onChange={handleChange}
            className={fieldClass}
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gideon-parchment mb-1.5">{fields.availability}</label>
        <input
          type="text"
          name="availability"
          value={formData.availability}
          onChange={handleChange}
          placeholder="e.g. Weekends, Evenings"
          className={fieldClass}
        />
      </div>
      <div className="flex items-start">
        <input
          type="checkbox"
          name="prayerTripletParticipation"
          id="prayerTriplet"
          checked={formData.prayerTripletParticipation}
          onChange={handleChange}
          className="mt-1 h-4 w-4 shrink-0 text-prayer-red focus:ring-prayer-red border-gideon-khaki/50 rounded"
        />
        <label htmlFor="prayerTriplet" className="ml-3 text-sm text-gideon-parchment leading-relaxed">
          {fields.prayerTriplet}
        </label>
      </div>
      <div className="rounded-xl border border-gray-300/80 bg-white p-4 space-y-3 shadow-sm">
        <p className="text-sm font-semibold text-gray-900">{t('gideon.enlistment.stayConnected', 'Stay connected')}</p>
        <div className="flex items-start">
          <input
            type="checkbox"
            name="promotionsEmailOptIn"
            id="promotionsEmail"
            checked={formData.promotionsEmailOptIn}
            onChange={handleChange}
            className="mt-1 h-4 w-4 shrink-0 text-prayer-red focus:ring-prayer-red border-gray-400 rounded"
          />
          <label htmlFor="promotionsEmail" className="ml-3 text-sm text-gray-900 leading-relaxed">
            {fields.promotionsEmail}
          </label>
        </div>
        <div className="flex items-start">
          <input
            type="checkbox"
            name="whatsappOptIn"
            id="whatsappOptIn"
            checked={formData.whatsappOptIn}
            onChange={handleChange}
            className="mt-1 h-4 w-4 shrink-0 text-prayer-red focus:ring-prayer-red border-gray-400 rounded"
          />
          <label htmlFor="whatsappOptIn" className="ml-3 text-sm text-gray-900 leading-relaxed">
            {fields.whatsappOptIn}
          </label>
        </div>
      </div>
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full sm:w-auto px-8 py-3 bg-prayer-red text-white rounded-xl font-medium hover:bg-prayer-red-light disabled:opacity-50"
      >
        {isLoading ? t('common.loading') : (copy.submit || t('gideon.enlistment.submit'))}
      </button>
    </form>
  );
};

export default GideonEnlistmentForm;
